import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './style/ForgotPassword.css'; // External CSS

const ForgotPassword = () => {
    // ========== STATE MANAGEMENT ==========
    const [step, setStep] = useState('email'); // 'email' | 'otp' | 'success'
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [timer, setTimer] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const [otpAttempts, setOtpAttempts] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    const otpInputs = useRef([]);
    const timerInterval = useRef(null);

    // ========== TIMER EFFECT ==========
    useEffect(() => {
        if (timer > 0) {
            timerInterval.current = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(timerInterval.current);
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            setCanResend(true);
        }

        return () => {
            if (timerInterval.current) {
                clearInterval(timerInterval.current);
            }
        };
    }, [timer]);

    // ========== OTP INPUT HANDLING ==========
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(0, 1);
        setOtp(newOtp);

        if (value && index < 5) {
            otpInputs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputs.current[index - 1].focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setOtp(digits);
            otpInputs.current[5].focus();
        }
    };

    // ========== API CALLS ==========
    const API_BASE = 'https://mkbackend-production.up.railway.app';

    const handleSendOTP = async (e) => {
        e.preventDefault();
        
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await fetch(`${API_BASE}/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (data.success) {
                setSuccessMessage('✅ OTP sent successfully! Check your email.');
                setStep('otp');
                setTimer(600);
                setCanResend(false);
                setOtpAttempts(0);
                setError('');
            } else {
                setError(data.message || 'Failed to send OTP. Please try again.');
            }
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        }

        setLoading(false);
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setError('Please enter the complete 6-digit OTP');
            return;
        }

        if (!newPassword) {
            setError('Please enter a new password');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await fetch(`${API_BASE}/verify-reset-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email, 
                    otp: otpString, 
                    newPassword, 
                    confirmPassword 
                })
            });

            const data = await response.json();

            if (data.success) {
                setSuccessMessage('✅ Password reset successful!');
                setStep('success');
                setError('');
            } else {
                setError(data.message || 'Failed to reset password');
                if (data.message && data.message.includes('attempts')) {
                    setOtpAttempts(prev => prev + 1);
                }
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }

        setLoading(false);
    };

    const handleResendOTP = async () => {
        if (!canResend) return;

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await fetch(`${API_BASE}/resend-reset-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (data.success) {
                setSuccessMessage('✅ New OTP sent successfully!');
                setTimer(600);
                setCanResend(false);
                setOtp(['', '', '', '', '', '']);
                setError('');
            } else {
                setError(data.message || 'Failed to resend OTP');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }

        setLoading(false);
    };

    // ========== RENDER FUNCTIONS ==========

    // Step 1: Email Input
    const renderEmailStep = () => (
        <div className="forgot-password-container">
            <div className="card">
                <div className="card-header">
                    <h2>🔐 Forgot Password</h2>
                    <p>Enter your email address to receive a password reset OTP</p>
                </div>

                <form onSubmit={handleSendOTP} className="form">
                    <div className="form-group">
                        <label>Email Address</label>
                        <input
                            type="email"
                            className="input-field"
                            placeholder="Enter your registered email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            required
                        />
                    </div>

                    {error && (
                        <div className="error-message">
                            <span>❌</span> {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="success-message">
                            <span>✅</span> {successMessage}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Sending...
                            </>
                        ) : (
                            'Send OTP'
                        )}
                    </button>

                    <div className="back-to-login">
                        <Link to="/login">← Back to Login</Link>
                    </div>
                </form>
            </div>
        </div>
    );

    // Step 2: OTP Verification
    const renderOTPStep = () => (
        <div className="forgot-password-container">
            <div className="card">
                <div className="card-header">
                    <h2>📧 Verify OTP</h2>
                    <p>Enter the 6-digit code sent to <strong>{email}</strong></p>
                    {timer > 0 && (
                        <p className="timer">
                            ⏱️ {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')} remaining
                        </p>
                    )}
                    {otpAttempts > 0 && (
                        <p className="attempts">
                            Attempts: {otpAttempts}/5
                        </p>
                    )}
                </div>

                <form onSubmit={handleVerifyOTP} className="form">
                    <div className="otp-container">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (otpInputs.current[index] = el)}
                                type="text"
                                className="otp-input"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                onPaste={index === 0 ? handleOtpPaste : undefined}
                                disabled={loading}
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>

                    <div className="form-group">
                        <label>New Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className="input-field"
                                placeholder="Enter new password (min 6 characters)"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={loading}
                                required
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? '🙈' : '👁️'}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Confirm Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                className="input-field"
                                placeholder="Confirm your new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                                required
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? '🙈' : '👁️'}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            <span>❌</span> {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="success-message">
                            <span>✅</span> {successMessage}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Verifying...
                            </>
                        ) : (
                            'Reset Password'
                        )}
                    </button>

                    <div className="resend-section">
                        {canResend ? (
                            <button 
                                type="button" 
                                className="btn-resend"
                                onClick={handleResendOTP}
                                disabled={loading}
                            >
                                Resend OTP
                            </button>
                        ) : (
                            <p className="resend-timer">
                                Resend available in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
                            </p>
                        )}
                    </div>

                    <div className="back-to-login">
                        <button 
                            type="button" 
                            className="btn-back"
                            onClick={() => setStep('email')}
                            disabled={loading}
                        >
                            ← Change Email
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    // Step 3: Success
    const renderSuccessStep = () => (
        <div className="forgot-password-container">
            <div className="card success-card">
                <div className="success-icon">✅</div>
                <h2>Password Reset Successful!</h2>
                <p>Your password has been changed successfully.</p>
                <div className="success-details">
                    <p>You can now login with your new password.</p>
                </div>

                <Link to="/login" className="btn-primary">
                    Go to Login →
                </Link>

                <div className="security-note">
                    <span>🔒</span>
                    <p>If you didn't make this change, please contact support immediately.</p>
                </div>
            </div>
        </div>
    );

    // ========== MAIN RENDER ==========
    if (step === 'email') return renderEmailStep();
    if (step === 'otp') return renderOTPStep();
    if (step === 'success') return renderSuccessStep();

    return null;
};

export default ForgotPassword;